import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import PageTitle from "./PageTitleCompoent";
import DoctorAppointmentCard from "./DoctorAppointmentCard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import { IoMdAdd } from "react-icons/io";
import api from "../api"; // ✅ centralized API instance
import "../css/DoctorAppointmentScreen.css";

const tabs = ["Home Visit", "Video Call", "Clinical Visit"];

export default function DoctorAppointmentScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const addedRef = useRef(false);

  const [packages, setPackages] = useState({
    "Home Visit": [],
    "Video Call": [],
    "Clinical Visit": [],
  });
  const [activeTab, setActiveTab] = useState("Home Visit");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editingPackage, setEditingPackage] = useState(null);
  const [deletePackageData, setDeletePackageData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // === Fetch packages by tab ===
  const fetchPackages = async (tab) => {
    try {
      setLoading(true);
      setError(null);

      let url = "/doctor-appointment-packages/";
      let params = {};

      if (tab === "Video Call") {
        params = { package_type: "video_call" };
      } else if (tab === "Clinical Visit") {
        params = { package_type: "clinical_visit" };
      }
      // For "Home Visit", no params are needed

      const res = await api.get(url, { params });

      if (res.data?.doctor_appointment_packages) {
        const formatted = res.data.doctor_appointment_packages.map((pkg) => ({
          id: pkg.id,
          title: pkg.package_name,
          subscribedPrice: `${pkg.subscribed_user_offer_amount}`,
          unsubscribedPrice: `${pkg.package_amount}`,
          tag: pkg.tags,
          points: pkg.services ? pkg.services.split(",") : [],
        }));

        setPackages((prev) => ({ ...prev, [tab]: formatted }));
      }
    } catch (err) {
      console.error("Failed to fetch doctor appointment packages:", err);
      setError("Failed to load packages.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch when active tab changes
  useEffect(() => {
    fetchPackages(activeTab);
  }, [activeTab]);

  // Handle package addition from AddPackage screen
  useEffect(() => {
    const newPackage = location.state?.newPackage;
    if (newPackage && !addedRef.current) {
      setPackages((prev) => {
        const exists = Object.values(prev)
          .flat()
          .some((pkg) => pkg.id === newPackage.id);
        if (exists) return prev;

        return {
          ...prev,
          [activeTab]: prev[activeTab]
            ? [...prev[activeTab], newPackage]
            : [newPackage],
        };
      });

      addedRef.current = true;
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, activeTab]);

  // Edit logic
  const handleEditClick = (pkg) => {
    setEditingPackage({ ...pkg, points: [...pkg.points] });
  };

  const handleServiceDelete = (index) => {
    setEditingPackage((prev) => ({
      ...prev,
      points: prev.points.filter((_, i) => i !== index),
    }));
  };

  const handleServiceAdd = (newPoint) => {
    if (newPoint.trim()) {
      setEditingPackage((prev) => ({
        ...prev,
        points: [...prev.points, newPoint.trim()],
      }));
    }
  };

  const handleSave = (services, subscribedPrice, unsubscribedPrice) => {
    setPackages((prev) => {
      const updatedTabPackages = prev[activeTab].map((pkg) =>
        pkg.id === editingPackage.id
          ? {
              ...editingPackage,
              points: services,
              subscribedPrice,
              unsubscribedPrice,
            }
          : pkg
      );
      return { ...prev, [activeTab]: updatedTabPackages };
    });
    setEditingPackage(null);
  };

  // Delete logic
  const handleDelete = (pkg) => {
    setDeletePackageData(pkg);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/doctor-appointment-packages/${deletePackageData.id}/`);

      setPackages((prev) => {
        const updatedTabPackages = prev[activeTab].filter(
          (pkg) => pkg.id !== deletePackageData.id
        );
        return { ...prev, [activeTab]: updatedTabPackages };
      });
    } catch (err) {
      console.error("Failed to delete package:", err);
      alert("Failed to delete package. Please try again.");
    } finally {
      setDeletePackageData(null);
      setIsDeleteModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setDeletePackageData(null);
    setIsDeleteModalOpen(false);
  };

  const handleBack = () => navigate(-1);

  return (
    <div className="doctor-appointment-container">
      <Sidebar />
      <div className="doctor-appointment-main">
        <Header />
        <div className="doctor-appointment-content">
          <div className="heading-sec">
            <PageTitle title="Doctor Appointment" onBack={handleBack} />
            <div className="add-package-sec">
              <button
                className="add-package-btn"
                onClick={() =>
                  navigate("/add_doctorappointment", {
                    state: { type: "Doctor Appointment", tab: activeTab },
                  })
                }
              >
                <IoMdAdd className="add-doctor-appointment-icon" color="#EBA8BF" />{" "}
                Add Package
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="doctor-appointment-section">
            <div className="tab-navigation">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`tab-button ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Cards */}
            <div className="doctor-appointment-card-grid">
              {loading ? (
                <p>Loading packages...</p>
              ) : error ? (
                <p className="error-text">{error}</p>
              ) : (packages[activeTab] || []).length > 0 ? (
                packages[activeTab].map((pkg) => (
                  <DoctorAppointmentCard
                    key={pkg.id}
                    {...pkg}
                    onAddService={() => handleEditClick(pkg)}
                    onDelete={() => handleDelete(pkg)}
                  />
                ))
              ) : (
                <p className="no-packages-text">No packages found.</p>
              )}
            </div>
          </div>

          {/* Edit Modal */}
         {editingPackage && (
  <div className="doctor-appointment-modal-overlay">
    <div className="doctor-appointment-modal-box">
      <DoctorAppointmentCard
        {...editingPackage}
        item={editingPackage}   // ✅ Pass the package object for saving
        isEditable={true}
        onClose={() => setEditingPackage(null)}
        onSave={handleSave}
        onAddService={handleServiceAdd}
        onDelete={handleServiceDelete}
      />
    </div>
  </div>
)}


          {/* Delete Confirmation */}
          <DeleteWithConfirmation
            isOpen={isDeleteModalOpen}
            onCancel={closeDeleteModal}
            onConfirm={confirmDelete}
            title={`Delete ${deletePackageData?.title || "Package"}?`}
            message="Are you sure you want to delete this package? This action cannot be undone."
          />
        </div>
      </div>
    </div>
  );
}   